import React, { useState, useEffect } from 'react';
import api from '../services/api';

const FlashcardRevisionPage = ({ onVoltar, token, user }) => {
  const [currentFlashcard, setCurrentFlashcard] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);

  // Function to get the next flashcard
  const getNextFlashcard = async () => {
    try {
      const response = await api.get('/api/flashcards/review/next', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setCurrentFlashcard(response.data.flashcard);
        setShowAnswer(false);
        setError(null);
      } else {
        setError(response.data.message || 'Nenhum flashcard disponível para revisão');
        setCurrentFlashcard(null);
      }
    } catch (err) {
      console.error('Erro ao obter próximo flashcard:', err);
      setError('Erro ao carregar flashcard. Tente novamente.');
      setCurrentFlashcard(null);
    } finally {
      // Reset loading state - the calling function (either useEffect or handleReview)
      // is responsible for setting the appropriate loading state
      // We still need to ensure loading states are reset in error cases
      // but we'll let the calling function manage the normal flow
    }
  };

  // Function to get review stats
  const loadStats = async () => {
    try {
      const response = await api.get('/api/flashcards/review/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  // Function to handle flashcard review
  const handleReview = async (difficulty) => {
    if (!currentFlashcard) return;

    setReviewLoading(true); // Set loading state before starting

    try {
      await api.post(`/api/flashcards/review/${currentFlashcard.flashcardId}`,
        { difficulty },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Get the next flashcard
      await getNextFlashcard();
      await loadStats();
    } catch (err) {
      console.error('Erro ao revisar flashcard:', err);
      setError('Erro ao revisar flashcard. Tente novamente.');
    } finally {
      // Always reset the review loading state, regardless of success or error
      setReviewLoading(false);
    }
  };

  // Load initial data when component mounts
  useEffect(() => {
    const checkAccessAndLoadData = async () => {
      try {
        // Check if user has access to flashcards section
        const accessResponse = await api.get('/api/section-access/check/flashcards', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (accessResponse.data.success && accessResponse.data.hasAccess) {
          setHasAccess(true);

          setLoading(true); // Set loading state for initial load
          loadStats();
          await getNextFlashcard();
        } else {
          setHasAccess(false);
        }
      } catch (err) {
        console.error('Erro ao verificar acesso ou carregar flashcards:', err);
        setHasAccess(false);
      } finally {
        setLoading(false);
        setAccessChecked(true);
      }
    };

    checkAccessAndLoadData();
  }, [token]);

  // Function to handle upgrade
  const handleUpgrade = () => {
    // Redirect to sales page
    onVoltar('venda-premium');
  };

  if (!accessChecked) {
    return (
      <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#9B4DFF] mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando acesso...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#F8F6FC]">
        {/* Loading overlay for review transitions */}
        {reviewLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-xl flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B4DFF] mb-4"></div>
              <p className="text-gray-700 font-medium">Carregando próximo flashcard...</p>
            </div>
          </div>
        )}
        <div className="container mx-auto p-4 sm:p-6 md:p-8">
          {/* Header */}
          <header className="flex items-center mb-8">
            <button
              onClick={onVoltar}
              className="flex items-center text-violet-600 font-semibold hover:text-violet-800 transition-colors mr-4"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
              Voltar
            </button>
            <h1 className="text-3xl font-bold text-gray-800">Revisar Flashcards</h1>
          </header>

          {/* Conteúdo principal com blur */}
          <div className="max-w-2xl mx-auto blur-sm pointer-events-none">
            {error && !currentFlashcard && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
                <span>{error}</span>
              </div>
            )}

            {currentFlashcard && (
              <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
                {/* Flashcard Front */}
                <div className="mb-8">
                  <div className="bg-gray-50 rounded-lg p-6 min-h-32 flex items-center">
                    <p className="text-lg text-gray-800 leading-relaxed">
                      {currentFlashcard.frente}
                    </p>
                  </div>
                </div>

                {/* Show Answer Button */}
                {!showAnswer && (
                  <div className="flex justify-center mb-8">
                    <button
                      className="bg-violet-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-violet-700 transition-colors"
                    >
                      Mostrar Resposta
                    </button>
                  </div>
                )}

                {/* Flashcard Back (only shown after clicking "Mostrar Resposta") */}
                {showAnswer && (
                  <>
                    <div className="mb-8">
                      <div className="bg-gray-50 rounded-lg p-6 min-h-32 flex items-center">
                        <p className="text-lg text-gray-800 leading-relaxed">
                          {currentFlashcard.verso}
                        </p>
                      </div>
                    </div>

                    {/* Difficulty Selection */}
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-gray-700 text-center mb-4">
                        Como foi essa revisão?
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          className="bg-red-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors"
                        >
                          De Novo
                        </button>
                        <button
                          className="bg-orange-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                        >
                          Difícil
                        </button>
                        <button
                          className="bg-blue-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
                        >
                          Bom
                        </button>
                        <button
                          className="bg-green-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors"
                        >
                          Fácil
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {error && currentFlashcard && (
              <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center text-red-800">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span>{error}</span>
                </div>
                <div className="mt-3 flex justify-center">
                  <button
                    className="bg-violet-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-violet-700 transition-colors"
                  >
                    Tentar Novamente
                  </button>
                </div>
              </div>
            )}

            {!currentFlashcard && !loading && (
              <div className="text-center py-12">
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 rounded-full bg-violet-100 flex items-center justify-center">
                    <svg className="w-12 h-12 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                    </svg>
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Nenhum flashcard para revisão</h2>
                <p className="text-gray-600 mb-8 max-w-md mx-auto text-lg">
                  Você não tem flashcards pendentes de revisão no momento. Continue estudando para gerar novos flashcards!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    className="bg-violet-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-violet-700 transition-colors shadow-lg"
                  >
                    Voltar ao Catálogo
                  </button>
                  <button
                    className="bg-white text-violet-600 border border-violet-600 px-8 py-4 rounded-xl font-semibold hover:bg-violet-50 transition-colors shadow-sm"
                  >
                    Atualizar
                  </button>
                </div>

                {/* Conteúdo editorial adicional para atender às políticas do AdSense */}
                <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-gray-200 max-w-2xl mx-auto">
                  <h3 className="font-semibold text-gray-800 mb-3">Sobre a Revisão de Flashcards</h3>
                  <p className="text-gray-600 mb-4 text-sm">
                    A revisão de flashcards é uma técnica poderosa baseada na repetição espaçada,
                    que ajuda a consolidar o conhecimento na memória de longo prazo.
                  </p>
                  <ul className="text-left text-gray-600 space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="text-violet-600 mr-2">✓</span>
                      <span>Responda honestamente sobre o quão bem você lembra do conteúdo</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-violet-600 mr-2">✓</span>
                      <span>Os flashcards difíceis aparecerão com mais frequência</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-violet-600 mr-2">✓</span>
                      <span>Os fáceis aparecerão com menos frequência</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-violet-600 mr-2">✓</span>
                      <span>Essa técnica maximiza sua eficiência de estudo</span>
                    </li>
                  </ul>
                </div>

                <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-gray-200 max-w-2xl mx-auto">
                  <h3 className="font-semibold text-gray-800 mb-3">Dicas para Revisão Efetiva</h3>
                  <div className="space-y-3 text-left text-gray-600 text-sm">
                    <p>1. <strong>Seja honesto:</strong> Avalie com precisão o quão bem você lembra do conteúdo</p>
                    <p>2. <strong>Mantenha o foco:</strong> Estude em um ambiente sem distrações</p>
                    <p>3. <strong>Seja consistente:</strong> Revise diariamente, mesmo que por pouco tempo</p>
                    <p>4. <strong>Pratique regularmente:</strong> A consistência é mais importante do que sessões longas</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Overlay com mensagem de acesso */}
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="text-center p-8 max-w-md bg-white rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold text-purple-600 mb-4">É necessário atualizar o seu plano para ter acesso</h2>
              <div className="mb-4">
                <button
                  onClick={handleUpgrade}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-6 rounded-full transition-colors duration-300 shadow-lg"
                >
                  Atualize seu Plano
                </button>
              </div>
              <button
                onClick={() => window.history.back()}
                className="text-gray-600 hover:text-gray-800 font-medium text-sm underline"
              >
                Voltar
              </button>
            </div>
          </div>
        </div>

        {/* Anúncio seguro após o conteúdo principal */}
      </div>
    );
  }

  if (loading && !currentFlashcard) {
    return (
      <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#9B4DFF] mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Carregando flashcards para revisão...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6FC]">
      {/* Loading overlay for review transitions */}
      {reviewLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B4DFF] mb-4"></div>
            <p className="text-gray-700 font-medium">Carregando próximo flashcard...</p>
          </div>
        </div>
      )}
      <div className="container mx-auto p-4 sm:p-6 md:p-8">
        {/* Header */}
        <header className="flex items-center mb-8">
          <button
            onClick={onVoltar}
            className="flex items-center text-violet-600 font-semibold hover:text-violet-800 transition-colors mr-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Voltar
          </button>
          <h1 className="text-3xl font-bold text-gray-800">Revisar Flashcards</h1>
        </header>


        {/* Main Content */}
        <div className="max-w-2xl mx-auto">
          {error && !currentFlashcard && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-6 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
              <span>{error}</span>
            </div>
          )}

          {currentFlashcard && (
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              {/* Flashcard Front */}
              <div className="mb-8">
                <div className="bg-gray-50 rounded-lg p-6 min-h-32 flex items-center">
                  <p className="text-lg text-gray-800 leading-relaxed">
                    {currentFlashcard.frente}
                  </p>
                </div>
              </div>

              {/* Show Answer Button */}
              {!showAnswer && (
                <div className="flex justify-center mb-8">
                  <button
                    onClick={() => setShowAnswer(true)}
                    className="bg-violet-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-violet-700 transition-colors"
                  >
                    Mostrar Resposta
                  </button>
                </div>
              )}

              {/* Flashcard Back (only shown after clicking "Mostrar Resposta") */}
              {showAnswer && (
                <>
                  <div className="mb-8">
                    <div className="bg-gray-50 rounded-lg p-6 min-h-32 flex items-center">
                      <p className="text-lg text-gray-800 leading-relaxed">
                        {currentFlashcard.verso}
                      </p>
                    </div>
                  </div>

                  {/* Difficulty Selection */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-gray-700 text-center mb-4">
                      Como foi essa revisão?
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handleReview('again')}
                        className="bg-red-300 text-white px-4 py-3 rounded-lg font-semibold hover:bg-red-400 transition-colors"
                      >
                        De Novo
                      </button>
                      <button
                        onClick={() => handleReview('hard')}
                        className="bg-orange-300 text-white px-4 py-3 rounded-lg font-semibold hover:bg-orange-400 transition-colors"
                      >
                        Difícil
                      </button>
                      <button
                        onClick={() => handleReview('good')}
                        className="bg-blue-300 text-white px-4 py-3 rounded-lg font-semibold hover:bg-blue-400 transition-colors"
                      >
                        Bom
                      </button>
                      <button
                        onClick={() => handleReview('easy')}
                        className="bg-green-300 text-white px-4 py-3 rounded-lg font-semibold hover:bg-green-400 transition-colors"
                      >
                        Fácil
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {error && currentFlashcard && (
            <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center text-red-800">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span>{error}</span>
              </div>
              <div className="mt-3 flex justify-center">
                <button
                  onClick={getNextFlashcard}
                  className="bg-violet-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-violet-700 transition-colors"
                >
                  Tentar Novamente
                </button>
              </div>
            </div>
          )}

          {!currentFlashcard && !loading && (
            <div className="text-center py-12">
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 rounded-full bg-violet-100 flex items-center justify-center">
                  <svg className="w-12 h-12 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                  </svg>
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Nenhum flashcard para revisão</h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto text-lg">
                Você não tem flashcards pendentes de revisão no momento. Continue estudando para gerar novos flashcards!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={onVoltar}
                  className="bg-violet-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-violet-700 transition-colors shadow-lg"
                >
                  Voltar ao Catálogo
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-white text-violet-600 border border-violet-600 px-8 py-4 rounded-xl font-semibold hover:bg-violet-50 transition-colors shadow-sm"
                >
                  Atualizar
                </button>
              </div>

              {/* Conteúdo editorial adicional para atender às políticas do AdSense */}
              <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-gray-200 max-w-2xl mx-auto">
                <h3 className="font-semibold text-gray-800 mb-3">Sobre a Revisão de Flashcards</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  A revisão de flashcards é uma técnica poderosa baseada na repetição espaçada,
                  que ajuda a consolidar o conhecimento na memória de longo prazo.
                </p>
                <ul className="text-left text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-violet-600 mr-2">✓</span>
                    <span>Responda honestamente sobre o quão bem você lembra do conteúdo</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-violet-600 mr-2">✓</span>
                    <span>Os flashcards difíceis aparecerão com mais frequência</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-violet-600 mr-2">✓</span>
                    <span>Os fáceis aparecerão com menos frequência</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-violet-600 mr-2">✓</span>
                    <span>Essa técnica maximiza sua eficiência de estudo</span>
                  </li>
                </ul>
              </div>

              <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-gray-200 max-w-2xl mx-auto">
                <h3 className="font-semibold text-gray-800 mb-3">Dicas para Revisão Efetiva</h3>
                <div className="space-y-3 text-left text-gray-600 text-sm">
                  <p>1. <strong>Seja honesto:</strong> Avalie com precisão o quão bem você lembra do conteúdo</p>
                  <p>2. <strong>Mantenha o foco:</strong> Estude em um ambiente sem distrações</p>
                  <p>3. <strong>Seja consistente:</strong> Revise diariamente, mesmo que por pouco tempo</p>
                  <p>4. <strong>Pratique regularmente:</strong> A consistência é mais importante do que sessões longas</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Anúncio seguro após o conteúdo principal */}
      </div>
    </div>
  );
};

export default FlashcardRevisionPage;